<template>
  <div id="container" class="map-container"></div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, PropType } from 'vue';
import AMapLoader from '@amap/amap-jsapi-loader';
import { Volcano } from '@/mock/volcanoes';

export default defineComponent({
  name: 'VolcanoMap',
  props: {
    volcanoes: {
      type: Array as PropType<Volcano[]>,
      required: true,
    },
  },
  setup(props) {
    const map = ref<any>(null);
    const markers = ref<any[]>([]);

    const initMap = async () => {
      try {
        const AMap = await AMapLoader.load({
          key: '673f4f155a06b5d609c4d81adbe28454',  // 需要替换为实际的Key
          version: '2.0',
          plugins: ['AMap.Marker', 'AMap.InfoWindow'],
        });

        map.value = new AMap.Map('container', {
          zoom: 5,
          center: [-170, 53],  // 调整中心点到阿拉斯加火山群的中心
          viewMode: '2D',
          showIndoorMap: false,
          pitch: 0,  // 俯仰角度
        });

        // 清除现有标记
        markers.value.forEach(marker => {
          marker.setMap(null);
        });
        markers.value = [];

        // 添加新标记
        props.volcanoes.forEach(volcano => {
          const iconColor = getAlertLevelColor(volcano.status.alertLevel);
          const marker = new AMap.Marker({
            position: [volcano.location.longitude, volcano.location.latitude],
            title: volcano.name,
            label: {
              content: volcano.name,
              direction: 'top',
              offset: new AMap.Pixel(0, -25),  // 调整标签位置
              style: {
                background: 'white',
                border: '1px solid #ccc',
                padding: '2px 5px',
                borderRadius: '3px',
              }
            },
            content: `<div style="
              background-color: ${iconColor}; 
              width: 20px; 
              height: 20px; 
              border-radius: 50%; 
              border: 3px solid white;
              box-shadow: 0 0 5px rgba(0,0,0,0.3);
            "></div>`,
            anchor: 'center',
          });

          marker.setMap(map.value);
          markers.value.push(marker);

          // 添加点击事件显示信息窗体
          marker.on('click', () => {
            const content = `
              <div>
                <h3>${volcano.name}</h3>
                <p>警戒级别: ${volcano.status.alertLevel}</p>
                <p>航空代码: ${volcano.status.aviationColorCode}</p>
                <p>描述: ${volcano.status.description}</p>
                <p>最后更新: ${new Date(volcano.status.lastUpdate).toLocaleString()}</p>
              </div>
            `;

            const infoWindow = new AMap.InfoWindow({
              content: content,
              offset: new AMap.Pixel(0, -30),
            });

            infoWindow.open(map.value, marker.getPosition());
          });
        });

        // 添加完所有标记后，调整视图以包含所有标记
        map.value.setFitView(null, {
          padding: [100, 100, 100, 100]  // 设置边距，确保标记不会太靠近地图边缘
        });
      } catch (error) {
        console.error('地图初始化失败:', error);
      }
    };

    const getAlertLevelColor = (level: string) => {
      const colors: Record<string, string> = {
        NORMAL: '#52c41a',
        ADVISORY: '#1890ff',
        WATCH: '#faad14',
        WARNING: '#ff4d4f',
      };
      return colors[level] || '#d9d9d9';
    };

    onMounted(() => {
      initMap();
    });

    watch(() => props.volcanoes, () => {
      if (map.value) {
        initMap();
      }
    }, { deep: true });

    return {
      map,
      markers,
    };
  },
});
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 500px;
}
</style>
